/**
 * autismModuleGating.js
 *
 * Pure utility functions that determine whether an Autism assessment module
 * card on the Patient Overview is locked (i.e. the clinician cannot enter it
 * yet because prerequisite steps are incomplete).
 *
 * Design rationale:
 *   ADHD's PatientOverview computes lock state inline, reading directly from
 *   the monolithic dataModel (e.g. dataModel['6_manageReports'].ciReportApproved).
 *   Autism cannot do that because report approval flags live in the modular
 *   reportContent subcollection, not on the patient header. This utility
 *   provides the same inline-style lock/tooltip semantics but reads from the
 *   correct data source.
 *
 * Phase 0 scope:
 *   - DD locks until History, SSI, and Observation reports are approved (or DD
 *     has been completed at least once — the "re-edit bypass", matching ADHD).
 *   - PAD locks until DD is completed.
 *
 * All functions are pure (no React/Firebase dependencies) and fully testable.
 */

import {
  STAGE_TO_REPORT_PREFIX,
  getReportApprovedField,
  getEnabledAutismStages,
} from '../../../../constants/adultAutismStages';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Check whether a specific stage's report has been approved by the clinician.
 * Reads the `${prefix}ReportApproved` field from the corresponding
 * reportContent subcollection document.
 *
 * @param {Object} reportContent - Map of stageKey → reportContent doc data
 * @param {string} stageKey      - e.g. 'history', 'semiStructuredInterview'
 * @returns {boolean} true only when the flag is explicitly `true`
 */
export function isStageReportApproved(reportContent, stageKey) {
  if (!reportContent || !STAGE_TO_REPORT_PREFIX[stageKey]) return false;
  const doc = reportContent[stageKey];
  if (!doc) return false;
  const field = getReportApprovedField(stageKey);
  return doc[field] === true;
}

/**
 * Human-readable labels for upstream reports that gate DD unlock.
 * `observation` is included here but only counts as a prerequisite when the
 * patient's workflow actually runs the Observation stage (see
 * getDDPrerequisiteStages) — orgs like Lancaster Medical Practice that disable
 * Observation unlock DD after History + SSI only.
 */
const ALL_DD_PREREQUISITE_STAGES = [
  { stageKey: 'history', label: 'History' },
  { stageKey: 'semiStructuredInterview', label: 'Semi-Structured Interview' },
  { stageKey: 'observation', label: 'Behavioural Observation' },
];

/**
 * Resolve the DD prerequisite stages that actually apply to this patient by
 * intersecting the full prerequisite list with the workflow's enabled stages.
 * Keeps DD unlock in lockstep with which cards the clinician can even complete.
 *
 * @param {Object} [wf] - Resolved workflow config (reads wf.isObservationEnabled)
 */
function getDDPrerequisiteStages(wf) {
  const enabledStages = getEnabledAutismStages(wf);
  return ALL_DD_PREREQUISITE_STAGES.filter(({ stageKey }) =>
    enabledStages.includes(stageKey),
  );
}

/**
 * Returns the display labels for prerequisite reports that are not yet approved.
 * Used to build the DD locked tooltip without hard-coding every combination.
 */
function getMissingApprovedPrerequisiteLabels(reportContent, wf) {
  return getDDPrerequisiteStages(wf)
    .filter(({ stageKey }) => !isStageReportApproved(reportContent, stageKey))
    .map(({ label }) => label);
}

/**
 * Formats a short list of stage names for tooltip copy, e.g.
 * ["History", "SSI"] → "History and SSI"
 * ["History", "SSI", "OBS"] → "History, SSI, and OBS"
 */
function formatStageListForTooltip(labels) {
  if (labels.length <= 1) return labels[0] || '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

// ─── Lock predicate ─────────────────────────────────────────────────────────

/**
 * Determine whether a module card should be locked on the Autism overview.
 *
 * Lock rules:
 *   diagnosticDecision:
 *     Locked UNLESS assessmentStages.diagnosticDecision.lastCompletedAt exists
 *     (one-way "re-edit bypass" — once DD is completed, it stays unlocked even
 *     if upstream reports are later un-approved during a re-edit cycle; mirrors
 *     ADHD's PatientOverview.shouldLockModule).
 *     Otherwise locked until historyReportApproved, ssiReportApproved, and
 *     obsReportApproved are all true.
 *
 *   postAssessmentDiscussion:
 *     Locked until assessmentStages.diagnosticDecision.status === 'completed'.
 *
 *   All other modules: never locked.
 *
 * @param {Object} params
 * @param {string} params.moduleKey       - Overview card key
 * @param {Object} params.reportContent   - Map of stageKey → reportContent doc
 * @param {Object} params.assessmentStages - Map of stageKey → assessmentStage doc
 * @param {Object} [params.wf]            - Resolved workflow config. Drives which
 *   upstream reports gate DD (Observation is only a prerequisite when the
 *   patient's workflow runs it — see getDDPrerequisiteStages).
 * @returns {boolean}
 */
export function shouldLockModule({ moduleKey, reportContent, assessmentStages, wf }) {
  switch (moduleKey) {
    case 'diagnosticDecision': {
      const ddPreviouslyCompleted = Boolean(
        assessmentStages?.diagnosticDecision?.lastCompletedAt,
      );
      if (ddPreviouslyCompleted) return false;

      // Locked until EVERY applicable prerequisite report is approved. For
      // Observation-enabled orgs (e.g. TOC) that means History + SSI + OBS; for
      // orgs with Observation disabled (e.g. LMP) it collapses to History + SSI.
      return getDDPrerequisiteStages(wf).some(
        ({ stageKey }) => !isStageReportApproved(reportContent, stageKey),
      );
    }

    case 'postAssessmentDiscussion':
      return assessmentStages?.diagnosticDecision?.status !== 'completed';

    default:
      return false;
  }
}

// ─── Tooltip ────────────────────────────────────────────────────────────────

/**
 * Build a human-readable tooltip explaining why a module is locked.
 * Returns an empty string when the module is not locked.
 *
 * @param {Object} params - Same shape as shouldLockModule
 * @returns {string}
 */
export function getLockedTooltip({ moduleKey, reportContent, assessmentStages, wf }) {
  if (!shouldLockModule({ moduleKey, reportContent, assessmentStages, wf })) return '';

  if (moduleKey === 'diagnosticDecision') {
    const missing = getMissingApprovedPrerequisiteLabels(reportContent, wf);
    if (missing.length > 0) {
      return `The ${formatStageListForTooltip(missing)} AI outputs need to be reviewed and approved for this module to become unlocked.`;
    }
  }

  if (moduleKey === 'postAssessmentDiscussion') {
    return 'The MDT Review / Diagnostic Decision must be completed before this module can be started.';
  }

  return '';
}
